import { useEffect, useState } from 'react'
import { Modal, ModalDescription, ModalTitle } from '@/components/modal/Modal'
import { ModalContainer } from '@/components/modal/ModalContainer'
import { ModalFooterWithActions } from '@/components/modal/ModalFooterWithActions'
import { type ExternalStorageProvider } from '@/constants/constants-external-storage'
import AppIcon from '@/settings/components/AppIcon'
import { PROVIDER_META } from '@/user_files/external-storage/ProviderMeta'
import { kPaxtonName } from '@/constants/constants-strings'
import { DropboxAccountRole } from '@/settings/fetch/get-dropbox-authorize-url'

function DropboxAccountRoleCard({
  description,
  label,
  role,
  selected,
  onSelect,
}: {
  description: string
  label: string
  role: DropboxAccountRole
  selected: boolean
  onSelect: (role: DropboxAccountRole) => void
}) {
  return (
    <label
      className={`flex cursor-pointer flex-col gap-1 rounded-lg border p-4 ${
        selected ? 'border-brand-500 bg-brand-50' : 'border-border-default bg-surface-emphasis'
      }`}
    >
      <span className="flex items-center gap-2">
        <input
          className="h-5 w-5 border-brand-neutral-300 text-brand-500 focus:ring-brand-500"
          type="radio"
          name="dropbox-account-role"
          value={role}
          aria-label={label}
          checked={selected}
          onChange={() => onSelect(role)}
        />
        <span className="font-rethink-sans text-base font-medium text-text-primary">{label}</span>
      </span>
      <span className="pl-7 text-sm text-brand-neutral-700">{description}</span>
    </label>
  )
}

export default function ConnectRedirectDialog({
  provider,
  open,
  loading,
  onOpenChange,
  onContinue,
}: {
  provider: ExternalStorageProvider | null
  open: boolean
  loading: boolean
  onOpenChange: (open: boolean) => void
  onContinue: (dropboxAccountRole?: DropboxAccountRole) => void
}) {
  const [dropboxAccountRole, setDropboxAccountRole] = useState<DropboxAccountRole | null>(null)

  useEffect(() => {
    setDropboxAccountRole(null)
  }, [open, provider])

  if (!provider) return null

  const meta = PROVIDER_META[provider]
  const isDropbox = provider === 'dropbox'

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      {/* Opened from within the SettingsModal, so stack above its overlay/content (repo's second-dialog pattern). */}
      <ModalContainer width="lg" height={isDropbox ? 'max-full' : 'max-sm'} stacked className="p-6" data-ds-component="Modal">
        <div className="min-h-0 overflow-auto">
          <div className="mb-6 flex items-center justify-center gap-2">
            <AppIcon />
            <div className="flex items-center gap-1.5" aria-hidden="true">
              <span className="h-[5px] w-[5px] rounded-full bg-brand-neutral-300" />
              <span className="h-[5px] w-[5px] rounded-full bg-brand-neutral-300" />
              <span className="h-[5px] w-[5px] rounded-full bg-brand-neutral-300" />
            </div>
            <div className="flex h-[60px] w-[60px] shrink-0 items-center justify-center rounded-md border border-border-default bg-surface-emphasis">
              {meta.modalIcon}
            </div>
          </div>

          <ModalTitle className="text-center">Connect {meta.label}</ModalTitle>
          <ModalDescription className="mx-auto mt-1.5 max-w-[520px] text-center text-base tracking-[-0.32px] text-text-primary">
            You'll be redirected to {meta.label} to authorize the connection. This gives {kPaxtonName} access to your {meta.label}. No files are synced until
            you map folders to matters.
          </ModalDescription>

          {isDropbox && (
            <fieldset className="mt-6 flex flex-col gap-3">
              <legend className="mb-2 text-sm font-medium text-text-primary">Which Dropbox account do you want to connect?</legend>
              <DropboxAccountRoleCard
                role={DropboxAccountRole.WORK}
                label="Work account"
                description="A Dropbox Business account, including team-space folders you can access."
                selected={dropboxAccountRole === DropboxAccountRole.WORK}
                onSelect={setDropboxAccountRole}
              />
              <DropboxAccountRoleCard
                role={DropboxAccountRole.PERSONAL}
                label="Personal account"
                description="Your individual Dropbox account and its files."
                selected={dropboxAccountRole === DropboxAccountRole.PERSONAL}
                onSelect={setDropboxAccountRole}
              />
            </fieldset>
          )}
        </div>
        <div className="shrink-0 pt-6">
          <ModalFooterWithActions
            secondaryAction={{ label: 'Cancel', onClick: () => onOpenChange(false), disabled: loading }}
            primaryAction={{
              label: `Continue to ${meta.label}`,
              onClick: () => onContinue(dropboxAccountRole ?? undefined),
              disabled: isDropbox && dropboxAccountRole == null,
              loading,
            }}
          />
        </div>
      </ModalContainer>
    </Modal>
  )
}
